import type { SiblingItem } from '../types/nav';

/**
 * SF-169: single source of truth for which pages belong to the World Part, consumed by
 * `SiblingRow` (via `siblingGroups.ts`'s `WORLD_GROUP`) and by `WorldHub.tsx`'s `/world` hub
 * screen (SF-155). Mirrors `kingdomNav.ts`/`realmNav.ts`'s existing precedent.
 *
 * `WORLD_ITEMS[0]` must stay `kingdoms` — `WorldHub`'s desktop guard redirects to
 * `WORLD_ITEMS[0].route` (`worldNav.test.ts` asserts this explicitly).
 *
 * Known limitation: both destination pages accept an optional `:kingdomId` URL param
 * (`/world/kingdoms/:kingdomId?`, `/world/dossier/:kingdomId?`), but `route` here stays the bare
 * path — `SiblingRow` reuses this same field as a literal `<Link to>` target and for exact-match
 * active-pill highlighting, so it can't hold a param pattern without breaking navigation. This
 * means mobile sibling chrome (SiblingRow/MobileTabBar/floating-button suppression) only resolves
 * on the bare default-view URL, not on a specific `:kingdomId` sub-path — a pre-existing
 * constraint shared by every other registered group (none of which have parameterized members
 * today), not something new introduced here. A real fix would need `SiblingItem` to carry a
 * separate match-pattern field from its Link target, which is out of this spec's scope.
 */
export const WORLD_ITEMS: readonly SiblingItem[] = [
  { key: 'kingdoms', label: 'Kingdoms', route: '/world/kingdoms', pillar: 'neutral', iconPath: 'M3 5H13M8 5V14M4 8H12M5 11H11' },
  { key: 'dossier',  label: 'Dossier',  route: '/world/dossier',  pillar: 'neutral', iconPath: 'M4 3H12V13L8 11L4 13ZM6 6H10M6 8H10' },
  // SF-171: Target Finder — world-wide scouting search + attack handoff. Four-tick crosshair/
  // reticle icon, matching the existing icon set's straight-line-only drawing convention.
  { key: 'targetFinder', label: 'Target Finder', route: '/world/targets', pillar: 'neutral', iconPath: 'M8 1V4M8 12V15M1 8H4M12 8H15' },
  // SF-155: completes WORLD_ITEMS to match sidebarNav.ts's World section item-for-item.
  // route/pillar/iconPath copied verbatim from sidebarNav.ts, mirroring REALM_ITEMS/WAR_ITEMS/
  // KINGDOM_ITEMS's own precedent of never inventing new icons/pillars for a mirrored entry.
  { key: 'intel',      label: 'Intel Hub',   route: '/intel',        pillar: 'military', iconPath: 'M8 2C4.5 2 2 5 2 8H14C14 5 11.5 2 8 2ZM4 10L3 14M12 10L13 14M6 10V14M10 10V14' },
  { key: 'recentNews', label: 'Recent News', route: '/recent-news',  pillar: 'neutral',  iconPath: 'M2 4H14V12H2ZM5 7H11M5 9H9' },
  { key: 'relations',  label: 'Relations',   route: '/relations',    pillar: 'neutral',  iconPath: 'M4 4A2 2 0 108 4A2 2 0 104 4ZM2 10C2 8 4 7 6 7M10 7C12 7 14 8 14 10M10 4A2 2 0 1014 4' },
  // SF-159: the last World-section item — /rankings had a reserved, `disabled: true` sidebar
  // entry with no page until now.
  { key: 'rankings',   label: 'Rankings',    route: '/rankings',     pillar: 'neutral',  iconPath: 'M3 14V8H6V14M7 14V4H10V14M11 14V6H14V14' },
];
